// How the assistant names companies in a reply (agent-service `companyLinkFragment`):
// `[Company Name](company:<id>)` for a company it holds the id of. The company half of
// `person-mentions.ts`, and deliberately simpler: there is no `?choose` variant, because nothing
// asks the user which company it meant.
//
// What differs from people is what the link BECOMES. A person link renders the same wherever it
// sits — the name plus an info icon. A company link on a line of its own becomes a ROW (logo, name,
// reason) that opens the company page; one named inside a sentence stays plain text. So the line
// shape matters here, and the recognition of it lives in `AgentMarkdown`, which reads the parsed
// document rather than this text.

export interface CompanyMention {
  name: string;
  id: string;
}

const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";
// The href alone, as react-markdown hands it over. The id is required: a company link without one
// names nothing the client could look up or open, unlike a person candidate, which may be looked up
// by name.
export const COMPANY_HREF = new RegExp(`^company:(${UUID})$`);
// The destination forms CommonMark accepts and the parser therefore renders as a company link:
// bare, in angle brackets, and with a title after it. They have to agree — the ROWS come from the
// parsed document while the IDS come from this regex, so a form the parser accepts and this misses
// is a row that is never hydrated: permanently unlinked, permanently showing the model's own name,
// and left as raw link syntax by `unlinkCompanies` in an export.
const COMPANY_LINK = new RegExp(
  `\\[([^\\]\\n]+)\\]\\(\\s*<?company:(${UUID})>?(?:\\s+(?:"[^"]*"|'[^']*'|\\([^)]*\\)))?\\s*\\)`,
  "g",
);

/** The companies a reply names, each once, in the order they first appear. */
export function companyMentions(markdown: string): CompanyMention[] {
  const seen = new Map<string, CompanyMention>();
  for (const [, name, id] of markdown.matchAll(COMPANY_LINK)) {
    if (!seen.has(id!)) seen.set(id!, { name: name!.trim(), id: id! });
  }
  return [...seen.values()];
}

/** The reply with its company links as the names they show, for a file or a prompt where no row is. */
export function unlinkCompanies(markdown: string): string {
  return markdown.replace(COMPANY_LINK, (_match, name: string) => name);
}
